// ⌘K command palette — quick actions + nav (local) + live global search (server)
// `onAction(kind)` fires for the Create commands ('engagement'|'buyer_engagement'|'company'|'contact'|'task');
// the App hosts the actual drawers so creation works from any screen.
// `onAskAtlas(query)` hands the raw query to the Ask Atlas chat panel — the AI
// path for questions the entity search can't answer. ↑↓ move a selection over
// the rows and ↵ picks it; the default selection is the top record hit (a typed
// name means "open that record"), falling back to the Ask Atlas row when
// nothing matches.
// `onCommand(command, arg)` runs a command row ('record_layout' → 'v2'|'classic'); the App owns the effect.

// Recent picks — entity rows the user actually opened from the palette,
// persisted per-browser so the next ⌘K starts with them as suggestions.
const RECENTS_KEY = 'atlas:cmd-recents';
const RECENTS_MAX = 8;
const loadRecents = () => {
  try {
    const v = JSON.parse(localStorage.getItem(RECENTS_KEY) || '[]');
    return Array.isArray(v) ? v : [];
  } catch { return []; }
};
const pushRecent = (entry) => {
  const rest = loadRecents().filter((r) => !(r.target === entry.target && r.payload === entry.payload));
  try {
    localStorage.setItem(RECENTS_KEY, JSON.stringify([entry, ...rest].slice(0, RECENTS_MAX)));
  } catch { /* storage full / private mode — recents just don't persist */ }
};

// The "Record layout" rows answer a deliberate query only: every word starts
// record / layout / new / classic / preview / use / team, and a word of 4+
// letters starts "record" or "layout" ("reco", "layout new", "team layout");
// "co", "new", "out" or "preview" never show them.
const LAYOUT_WORDS = ['record', 'layout', 'new', 'classic', 'preview', 'use', 'team'];
const isLayoutQuery = (query) => {
  const words = String(query || '').toLowerCase().split(/[\s:]+/).filter(Boolean);
  return words.length > 0
    && words.every((w) => LAYOUT_WORDS.some((t) => t.startsWith(w)))
    && words.some((w) => w.length >= 4 && ('record'.startsWith(w) || 'layout'.startsWith(w)));
};

const Cmd = ({ open, onClose, navigate, onAction, onAskAtlas, onCommand }) => {
  const [q, setQ] = React.useState('');
  const [searchResults, setSearchResults] = React.useState(null);
  const [searching, setSearching] = React.useState(false);
  const [recents, setRecents] = React.useState([]);
  // Keyboard selection: the explicitly chosen row id (arrows/hover), or null =
  // follow the default (top record hit → recent → exact nav → Ask Atlas).
  const [selId, setSelId] = React.useState(null);
  // Enter pressed while the debounced search was still in flight — resolve it
  // against the fresh results instead of falling through to Ask Atlas.
  const [pendingEnter, setPendingEnter] = React.useState(false);
  const pickSelectedRef = React.useRef(() => {});
  const panelRef = React.useRef(null);
  const overlayRef = React.useRef(null);
  const titleId = React.useId();
  // Stay mounted through the exit animation (see Drawer for the pattern).
  const [mounted, setMounted] = React.useState(open);
  const openRef = React.useRef(open);
  openRef.current = open;
  React.useEffect(() => {
    if (open) { setMounted(true); return; }
    if (!panelRef.current) { setMounted(false); return; }
    Promise.all([
      window.animateEl(panelRef.current, { transform: 'scale(0.99)', opacity: 0 }),
      window.animateEl(overlayRef.current, { opacity: 0 }),
    ]).then(() => { if (!openRef.current) setMounted(false); });
  }, [open]);
  React.useLayoutEffect(() => {
    if (!open || !mounted) return;
    window.animateEl(panelRef.current, { transform: ['scale(0.99)', 'scale(1)'], opacity: [0, 1] });
    window.animateEl(overlayRef.current, { opacity: [0, 1] });
  }, [open, mounted]);

  useFocusTrap(open, panelRef);
  useEscapeKey(open, onClose);

  React.useEffect(() => {
    if (open) { setQ(''); setSearchResults(null); setRecents(loadRecents()); setSelId(null); setPendingEnter(false); }
  }, [open]);

  // A deferred Enter fires once the results for the CURRENT query are in
  // (searchResults carries _q). pickSelectedRef is re-assigned every render,
  // so this always picks against the fresh default selection.
  React.useEffect(() => {
    if (pendingEnter && searchResults && searchResults._q === q.trim()) {
      setPendingEnter(false);
      pickSelectedRef.current();
    }
  }, [pendingEnter, searchResults, q]);

  // Debounced live search against /api/search when the user types 3+ chars
  // (trigram indexes need >= 3 chars; the backend short-circuits below that).
  // `stale` guards the await: clearTimeout only cancels the un-fired timer,
  // not an in-flight request — without the guard a slow response for "acm"
  // could land after and overwrite the results for "acme".
  React.useEffect(() => {
    if (!open) return;
    const trimmed = q.trim();
    if (trimmed.length < 3) { setSearchResults(null); return; }
    let stale = false;
    const handle = setTimeout(async () => {
      setSearching(true);
      try {
        const r = await window.AtlasApi.search(trimmed, 12);
        // _q marks which query these results answer — a deferred Enter must
        // only resolve against results for what's actually in the box.
        if (!stale) setSearchResults({ ...r, _q: trimmed });
      } catch {
        if (!stale) setSearchResults({ results: [], _q: trimmed });
      } finally { if (!stale) setSearching(false); }
    }, 200);
    return () => { stale = true; clearTimeout(handle); };
  }, [q, open]);

  if (!mounted) return null;

  const Q = q.toLowerCase().trim();
  // Only offer pages (and creates) this role can open — same gate as the
  // sidebar and the + New menu, so search never links into a bounced page.
  const canGo = (page) => !window.AtlasCanSee || window.AtlasCanSee(page);
  const CREATE_PAGE = { engagement: 'deals', buyer_engagement: 'deals', company: 'companies', contact: 'people', task: 'inbox' };
  const matches = (s) => !Q || s.toLowerCase().includes(Q);

  // Build entity sections from live search results
  const liveResults = (searchResults?.results) || [];
  const grouped = { company: [], contact: [], engagement: [] };
  for (const r of liveResults) {
    if (grouped[r.entity_type]) grouped[r.entity_type].push(r);
  }
  const entityIcon = {
    company:    <Icon.Building size={13} />,
    contact:    <Icon.User size={13} />,
    engagement: <Icon.Diamond size={13} />,
  };
  const entityTarget = {
    company:    'company',   // app routes to CompanyDetail when given a company id
    contact:    'contact',   // app routes to ContactDetail when given a contact id
    engagement: 'deal',      // engagement maps to deal detail in our app router
  };
  // Recents store the nav target, not the entity type — map back for icons.
  const targetIcon = { company: entityIcon.company, contact: entityIcon.contact, deal: entityIcon.engagement };

  // Contacts belonging to a matched company nest under its row (the backend
  // sends each hit company's people as extra rank-0.5 results); the rest —
  // direct name/email/phone hits — keep their own Contacts section.
  const companyIds = new Set(grouped.company.map((r) => r.id));
  const contactsByCompany = {};
  const looseContacts = [];
  for (const r of grouped.contact) {
    if (r.company_id && companyIds.has(r.company_id)) {
      (contactsByCompany[r.company_id] = contactsByCompany[r.company_id] || []).push(r);
    } else looseContacts.push(r);
  }

  // `fuzzy` rows are the backend's closest-spelling fill for a type with no
  // (or too few) direct hits — "ascelus" → Ascellus. A section of nothing
  // but those says so in its title; a mixed one tags the rows.
  const allFuzzy = (rows) => rows.length > 0 && rows.every((r) => r.fuzzy);
  const sectionTitle = (base, rows) => base + (allFuzzy(rows) ? ' · closest matches' : '');

  // Record layout (window.recordLayoutState, views/record_panels.jsx): an admin's
  // own preview over the team's layout (Settings → Feature flags). Admins only, typed
  // only (isLayoutQuery); never for a deal guest or on a phone (phones keep their own pages).
  const layoutState = window.recordLayoutState ? window.recordLayoutState() : null;
  const layoutSection = !!onCommand && !!layoutState && layoutState.admin === true && !window.AtlasIsGuest
    && !(window.AtlasDevice && window.AtlasDevice.phone) && isLayoutQuery(Q) && {
    title: 'Record layout',
    items: [
      { id: 'layout-v2', label: 'Preview record layout: new', command: 'record_layout', arg: 'v2' },
      { id: 'layout-classic', label: 'Preview record layout: classic', command: 'record_layout', arg: 'classic' },
      { id: 'layout-team', label: 'Use team layout', command: 'record_layout', arg: 'team' },
    ].map((i) => ({ ...i, kind: 'command', icon: <Icon.Layers size={13} />, checked: i.arg === (layoutState.preview || 'team') })),
  };

  // Record hits render ABOVE the Ask Atlas row: a name query's most likely
  // intent is opening the record, so that's also what plain Enter defaults to
  // (see `defaultItem`). Ask Atlas stays one ↓ away as the AI fallback.
  const sections = [
    grouped.company.length > 0 && {
      title: sectionTitle('Companies', grouped.company) + (searching ? ' (searching…)' : ''),
      mixed: !allFuzzy(grouped.company),
      items: grouped.company.flatMap((r) => [
        {
          id: 'co-' + r.id, icon: entityIcon.company, kind: 'entity',
          label: r.title, sub: r.subtitle, fuzzy: r.fuzzy,
          target: entityTarget.company, payload: r.id,
        },
        ...(contactsByCompany[r.id] || []).map((ct) => ({
          id: 'ct-' + ct.id, icon: entityIcon.contact, kind: 'entity',
          label: ct.title, sub: ct.subtitle,
          target: entityTarget.contact, payload: ct.id, indent: true,
          // Rides along with a closest-spelling company: not a direct hit
          // either, so it mustn't win the Enter default over its parent.
          underFuzzy: r.fuzzy,
        })),
      ]),
    },
    looseContacts.length > 0 && {
      title: sectionTitle('Contacts', looseContacts),
      mixed: !allFuzzy(looseContacts),
      items: looseContacts.map((r) => ({
        id: 'ct-' + r.id, icon: entityIcon.contact, kind: 'entity',
        // Employer first: four "Frank Murphy"s are told apart by company,
        // not by squinting at email domains.
        label: r.title, sub: [r.company_name, r.subtitle].filter(Boolean).join(' · '),
        fuzzy: r.fuzzy,
        target: entityTarget.contact, payload: r.id,
      })),
    },
    grouped.engagement.length > 0 && {
      title: sectionTitle('Engagements', grouped.engagement),
      mixed: !allFuzzy(grouped.engagement),
      items: grouped.engagement.map((r) => ({
        id: 'eng-' + r.id, icon: entityIcon.engagement, kind: 'entity',
        label: r.title, sub: r.subtitle, fuzzy: r.fuzzy,
        target: 'deal', payload: r.id,
      })),
    },
    // Below the live-search threshold, resurface the last entities the user
    // opened from here (typing 1-2 chars narrows them by label).
    Q.length < 3 && recents.length > 0 && {
      title: 'Recent',
      items: recents.filter((r) => matches(r.label)).map((r) => ({
        id: `recent-${r.target}-${r.payload}`,
        icon: targetIcon[r.target] || <Icon.Search size={13} />, kind: 'recent',
        label: r.label, sub: r.sub,
        target: r.target, payload: r.payload,
      })),
    },
    onAskAtlas && Q.length >= 2 && {
      title: 'Ask Atlas',
      items: [{
        id: 'ask-atlas',
        icon: <Icon.Sparkle size={13} />, kind: 'ask',
        label: `Ask Atlas: “${q.trim()}”`,
        ask: true,
      }],
    },
    {
      title: 'Navigate',
      items: [
        { id: 'go-home',      icon: <Icon.Lightning size={13} />, label: 'Home',       target: 'home' },
        { id: 'go-inbox',     icon: <Icon.Check size={13} />,     label: 'Tasks',      target: 'inbox' },
        { id: 'go-calendar',  icon: <Icon.Calendar size={13} />,  label: 'Calendar',   target: 'calendar' },
        { id: 'go-deals',     icon: <Icon.Pipeline size={13} />,  label: 'Engagements',target: 'deals' },
        { id: 'go-companies', icon: <Icon.Building size={13} />,  label: 'Companies',  target: 'companies' },
        { id: 'go-people',    icon: <Icon.User size={13} />,      label: 'People',     target: 'people' },
        { id: 'go-industries', icon: <Icon.Layers size={13} />,   label: 'Industries', target: 'industries' },
        { id: 'go-agents',    icon: <Icon.Sparkle size={13} />,   label: 'Agents',     target: 'agents' },
        { id: 'go-settings',  icon: <Icon.Settings size={13} />,  label: 'Settings',   target: 'settings' },
      ].filter(i => matches(i.label) && canGo(i.target)).map(i => ({ ...i, kind: 'nav' })),
    },
    {
      title: 'Create',
      items: [
        { id: 'new-engagement', icon: <Icon.Pipeline size={13} />, label: 'New engagement', action: 'engagement' },
        { id: 'new-buyer-engagement', icon: <Icon.Pipeline size={13} />, label: 'New buyer engagement', action: 'buyer_engagement' },
        { id: 'new-company',    icon: <Icon.Building size={13} />, label: 'New company',    action: 'company' },
        { id: 'new-contact',    icon: <Icon.User size={13} />,     label: 'New contact',    action: 'contact' },
        { id: 'new-task',       icon: <Icon.Check size={13} />,    label: 'New task',       action: 'task' },
        // The extra tokens let "create …" / "add …" queries hit these too.
      ].filter(i => matches(`${i.label} create add reminder`) && canGo(CREATE_PAGE[i.action])).map(i => ({ ...i, kind: 'nav' })),
    },
    layoutSection,
    Q.length >= 3 && liveResults.length === 0 && !searching && {
      title: 'No matches',
      items: [{ id: 'no-results', icon: <Icon.Search size={13} />, label: `No companies, contacts, or engagements match "${q}"`, sub: 'Try a shorter query or check spelling.' }],
    },
  ].filter(Boolean).filter(s => s.items.length);

  const onPick = (item) => {
    if (item.ask) { onAskAtlas && onAskAtlas(q.trim()); }
    else if (item.action) { onAction && onAction(item.action); }
    else if (item.command) { onCommand && onCommand(item.command, item.arg); }
    else if (item.target) {
      // Entity rows carry a payload (id); Navigate rows don't — only the
      // former are worth resurfacing as recents.
      if (item.payload) {
        pushRecent({ target: item.target, payload: item.payload, label: item.label, sub: item.sub || null });
      }
      navigate(item.target, item.payload);
    }
    onClose();
  };

  // What Enter acts on. selId (arrows/hover) wins; otherwise the default is
  // the top record hit — a typed name most likely means "open that record" —
  // then a matching recent, an exact Navigate/Create label, a closest-
  // spelling hit (a typo'd name), and only then Ask Atlas (so question-style
  // queries still reach the AI on plain Enter — they're too long to
  // typo-match a record name).
  const flatItems = sections.flatMap((s) => s.items).filter((it) => it.ask || it.action || it.target || it.command);
  const defaultItem =
    flatItems.find((it) => it.kind === 'entity' && !it.fuzzy && !it.underFuzzy)
    || flatItems.find((it) => it.kind === 'recent')
    || flatItems.find((it) => it.kind === 'nav' && it.label.toLowerCase() === Q)
    || flatItems.find((it) => it.kind === 'entity')
    || flatItems.find((it) => it.kind === 'ask')
    || flatItems[0]
    || null;
  const selected = flatItems.find((it) => it.id === selId) || defaultItem;
  pickSelectedRef.current = () => { if (selected) onPick(selected); };

  const moveSelection = (dir) => {
    if (!flatItems.length) return;
    const idx = selected ? flatItems.findIndex((it) => it.id === selected.id) : -1;
    const next = flatItems[(idx + dir + flatItems.length) % flatItems.length];
    setSelId(next.id);
    const el = document.getElementById('cmd-row-' + next.id);
    if (el) el.scrollIntoView({ block: 'nearest' });
  };

  const onInputKeyDown = (e) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      moveSelection(e.key === 'ArrowDown' ? 1 : -1);
      return;
    }
    if (e.key !== 'Enter') return;
    e.preventDefault();
    const trimmed = q.trim();
    // Enter inside the search debounce: don't commit to the AI row just
    // because the record results haven't landed yet — defer one beat and
    // resolve against the fresh default (see the pendingEnter effect).
    if (trimmed.length >= 3 && !selId && (!searchResults || searchResults._q !== trimmed)) {
      setPendingEnter(true);
      return;
    }
    if (selected) onPick(selected);
  };

  // Phone: a full-screen search page — sticky field + Cancel up top, the
  // results filling the rest (above the keyboard), two-line rows.
  const phone = !!(window.AtlasDevice && window.AtlasDevice.phone);
  return (
    <div ref={overlayRef} onClick={onClose} style={phone ? {
      position: 'fixed', inset: 0, zIndex: 100, background: 'var(--surface)',
      display: 'flex', flexDirection: 'column', pointerEvents: open ? 'auto' : 'none',
    } : {
      position: 'fixed', inset: 0, zIndex: 100, background: 'oklch(0.2 0.01 270 / 0.32)',
      display: 'flex', alignItems: 'flex-start', justifyContent: 'center', paddingTop: '12vh',
      backdropFilter: 'blur(2px)', pointerEvents: open ? 'auto' : 'none',
    }}>
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onClick={(e) => e.stopPropagation()}
        style={phone ? {
          flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', background: 'var(--surface)',
          paddingTop: 'var(--sat)', paddingLeft: 'var(--sal)', paddingRight: 'var(--sar)',
        } : {
          width: 560, maxWidth: '92vw', background: 'var(--surface)', borderRadius: 12,
          boxShadow: 'var(--shadow-lg)', border: '1px solid var(--border)', overflow: 'hidden',
        }}
      >
        <h2 id={titleId} style={{ position: 'absolute', width: 1, height: 1, padding: 0, margin: -1, overflow: 'hidden', clip: 'rect(0,0,0,0)', whiteSpace: 'nowrap', border: 0 }}>
          Command palette
        </h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: phone ? '8px 6px 8px 16px' : '14px 16px', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
          <Icon.Search size={phone ? 18 : 15} color="var(--text-3)" />
          <input
            autoFocus
            type={phone ? 'search' : undefined}
            enterKeyHint={phone ? 'search' : undefined}
            autoCapitalize={phone ? 'off' : undefined}
            autoCorrect={phone ? 'off' : undefined}
            aria-label="Search companies, deals, people, or ask Atlas AI"
            value={q}
            onChange={(e) => { setQ(e.target.value); setSelId(null); setPendingEnter(false); }}
            onKeyDown={onInputKeyDown}
            placeholder={phone ? 'Search or ask Atlas…' : 'Search companies, deals, people, or ask Atlas anything…'}
            style={{
              flex: 1, minWidth: 0, border: 'none', outline: 'none', fontSize: phone ? 16 : 14, color: 'var(--text)',
              background: 'transparent', fontFamily: 'inherit', height: phone ? 40 : undefined,
            }}
          />
          {phone
            ? <button type="button" onClick={onClose} style={{ height: 40, padding: '0 10px', border: 'none', background: 'transparent', color: 'var(--accent-text)', fontSize: 16, fontWeight: 500, cursor: 'pointer', flexShrink: 0 }}>Cancel</button>
            : <Kbd>esc</Kbd>}
        </div>
        <div style={phone
          ? { flex: 1, minHeight: 0, overflowY: 'auto', WebkitOverflowScrolling: 'touch', padding: '6px 0', paddingBottom: 'calc(12px + max(var(--sab), var(--kb, 0px)))' }
          : { maxHeight: 380, overflowY: 'auto', padding: '6px 0' }}>
          {sections.length === 0 && (
            <div style={{ padding: '20px 16px', fontSize: 12.5, color: 'var(--text-3)', textAlign: 'center' }}>No results for "{q}"</div>
          )}
          {sections.map((sec) => (
            <div key={sec.title}>
              <div style={{ padding: '8px 16px 4px', fontSize: 10.5, fontWeight: 600, color: 'var(--text-4)', letterSpacing: '0.06em', textTransform: 'uppercase' }}>{sec.title}</div>
              {sec.items.map((it) => {
                const isSel = selected && selected.id === it.id;
                return (
                  <button
                    key={it.id}
                    id={'cmd-row-' + it.id}
                    onClick={() => onPick(it)}
                    // Hover moves the keyboard selection too — one highlight,
                    // and Enter always does what the highlight shows.
                    onMouseEnter={phone ? undefined : () => { if (it.ask || it.action || it.target || it.command) setSelId(it.id); }}
                    style={{
                      display: 'flex', alignItems: 'center', gap: phone ? 12 : 10, width: '100%',
                      // Indented rows = a company's contacts nested under it.
                      padding: phone ? (it.indent ? '8px 16px 8px 46px' : '10px 16px') : (it.indent ? '5px 16px 5px 40px' : '7px 16px'),
                      minHeight: phone ? 50 : undefined,
                      border: 'none', background: !phone && isSel ? 'var(--surface-hover)' : 'transparent',
                      cursor: 'pointer', textAlign: 'left',
                    }}
                  >
                    <span style={{ display: 'inline-flex', color: 'var(--text-3)' }}>{it.icon}</span>
                    {phone ? (
                      <span style={{ flex: 1, minWidth: 0 }}>
                        <span style={{ display: 'block', fontSize: 15.5, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{it.label}</span>
                        {it.sub && <span style={{ display: 'block', fontSize: 13, color: 'var(--text-3)', marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{it.sub}</span>}
                      </span>
                    ) : (
                    <span style={{ flex: 1, fontSize: 13, color: 'var(--text)' }}>
                      {it.label}
                      {it.sub && <span style={{ marginLeft: 6, color: 'var(--text-3)', fontSize: 12 }}>· {it.sub}</span>}
                    </span>
                    )}
                    {it.fuzzy && sec.mixed && (
                      <span style={{ fontSize: 11, color: 'var(--text-4)', whiteSpace: 'nowrap' }}>closest match</span>
                    )}
                    {it.checked && <Icon.Check size={12} color="var(--accent-text)" />}
                    {isSel && <Kbd>↵</Kbd>}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
        {!phone && <div style={{ padding: '8px 14px', borderTop: '1px solid var(--border)', display: 'flex', gap: 12, fontSize: 11, color: 'var(--text-3)', background: 'var(--surface-2)' }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><Kbd>↑↓</Kbd> select</span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
            <Kbd>↵</Kbd> {selected ? (selected.ask ? 'ask Atlas' : selected.action ? 'create' : selected.command ? 'switch' : 'open') : '—'}
          </span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><Kbd>esc</Kbd> close</span>
        </div>}
      </div>
    </div>
  );
};

window.Cmd = Cmd;
